import React, { useState, useRef, useEffect } from "react";
import Testimonial from "@/components/Testimonials/Testimonial";
import { Button } from "@/components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import "./TestimonialsSlider.css";

interface AlumniTestimonialsSliderProps {
  title: string;
  testimonials: { author: string; quote: string }[];
  testimonialCardClassName?: string;
  titleClassName?: string;
}

export default function AlumniTestimonialsSlider({
  title,
  testimonials,
  testimonialCardClassName,
  titleClassName = "",
}: AlumniTestimonialsSliderProps) {
  const carouselMessages = useCarouselMessages();

  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const slidesContainerRef = useRef<HTMLDivElement>(null);

  // CSS handles the transform via custom properties
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToNext = () => {
    // Always move 1 slide, CSS breakpoints handle the visual difference
    setCurrentIndex((prev) => Math.min(prev + 1, testimonials.length - 1));
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  // Handle touch/swipe events
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  return (
    <div>
      <h2
        className={cn(
          "mt-0 text-2xl md:text-3xl font-bold text-gray-800",
          titleClassName,
        )}
      >
        {title}
      </h2>

      <div className="w-full relative">
        <div
          ref={containerRef}
          className="relative overflow-x-hidden overflow-y-visible h-auto md:overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={slidesContainerRef}
            className="flex md:gap-8 transition-transform duration-300 ease-in-out h-auto md:h-full testimonials-slider"
            style={
              {
                "--translate-x": `${-currentIndex * 100}%`,
                "--gap-offset": `${-currentIndex * 0.5}rem`,
              } as React.CSSProperties
            }
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-putty-200 rounded-xl border border-putty-400 w-full md:w-[calc(33.333%-1.5rem)] flex-shrink-0 px-4 md:px-1 flex items-center justify-center"
              >
                <Testimonial
                  id={`testimonial-${index}`}
                  attribution={`— ${testimonial.author}`}
                  variant="light"
                  showQuote={true}
                  className={cn("w-full h-full", testimonialCardClassName)}
                >
                  <p className="text-base leading-relaxed">
                    {testimonial.quote}
                  </p>
                </Testimonial>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <div className="flex items-center bg-white rounded-xl border border-putty-400 px-2 py-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={goToPrevious}
            className="mr-6 md:mr-2 lg:mr-6 h-6 w-6 p-0 text-elm-500 hover:bg-transparent hover:text-elm-500 focus:bg-transparent focus:text-elm-500 focus:outline-none"
            disabled={currentIndex === 0}
          >
            <ChevronLeftIcon
              className="w-6 h-6 md:w-4 md:h-4 lg:w-6 lg:h-6 text-primary-500"
              aria-hidden="true"
            />
            <span className="sr-only">{carouselMessages.previousSlide}</span>
          </Button>
          <div className="flex gap-6 md:gap-2 lg:gap-6">
            {Array.from(
              {
                length: testimonials.length, // Always show all indicators
              },
              (_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={cn(
                    "w-3.5 h-3.5 rounded-full transition-all duration-200",
                    index === currentIndex
                      ? "bg-brand-500"
                      : "bg-secondary-500 hover:bg-secondary-400",
                  )}
                  aria-label={carouselMessages.goToSlide(index + 1)}
                />
              ),
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={goToNext}
            className="ml-6 md:ml-2 lg:ml-6 h-6 w-6 p-0 text-elm-500 hover:bg-transparent hover:text-elm-500 focus:bg-transparent focus:text-elm-500 focus:outline-none"
            disabled={currentIndex >= testimonials.length - 1}
          >
            <ChevronRightIcon
              className="w-6 h-6 md:w-4 md:h-4 lg:w-6 lg:h-6 text-primary-500"
              aria-hidden="true"
            />
            <span className="sr-only">{carouselMessages.nextSlide}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
